/**
 * Presentational field types for the ECM v2 design (TRUS-571, Layer 2).
 *
 * WHY THESE LIVE IN ONE MODULE
 * There are two renderers. A SUBMITTED record goes through FormRenderer, a DRAFT
 * through FormInstanceEditor, and they have already drifted — FormRenderer alone
 * knows `divider`, `spacer` and `billing_evaluator`; FormInstanceEditor alone knows
 * `multiselect`. An element built in only one of them works while the LCM is
 * filling the form and vanishes the moment it is submitted, or the reverse. Two
 * defects on this ticket were exactly that shape: the required asterisk (TRUS-566
 * #4) and the editable-after-submit note (#3).
 *
 * So every element here is written once and mounted by both. `renderFormElement`
 * returns null for a field_type it does not own, which is what lets each renderer
 * call it first and fall through to its own switch unchanged.
 *
 * ALL OF THESE ARE DISPLAY-ONLY. None collects input, so none needs a value, an
 * onChange or a place in the save path — which is why they can be added to a
 * template without touching com_fields or the submit flow. A field that looks
 * editable but is not would be TRUS-361 with the sides swapped, so they are
 * deliberately styled as content rather than as disabled controls.
 *
 * Styling goes through the --wf-* theme vars so an org's design template reaches
 * them; every one falls back to app chrome when no theme is set, because an
 * undefined theme is a correct render, not an error.
 */

import React from 'react';
import { Info } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'error';

const TONE_VAR: Record<Tone, { fg: string; bg: string }> = {
  neutral: { fg: 'var(--wf-muted, #6B7280)', bg: 'var(--wf-border, #E5E7EB)' },
  accent: { fg: 'var(--wf-accent, #2563EB)', bg: 'var(--wf-accent-soft, #EFF6FF)' },
  success: { fg: 'var(--wf-success, #16A34A)', bg: 'var(--wf-success-soft, #ECFDF5)' },
  warning: { fg: 'var(--wf-warning, #B45309)', bg: 'var(--wf-warning-soft, #FFFBEB)' },
  error: { fg: 'var(--wf-error, #DC2626)', bg: 'var(--wf-error-soft, #FEF2F2)' },
};

/** Map a free-text status onto a tone. The ECM statuses are the ones that matter;
 *  anything unrecognised stays neutral rather than guessing a colour for it. */
export function toneForStatus(value: string): Tone {
  const v = (value || '').toLowerCase();
  if (/complete|resolved|signed|done|yes/.test(v)) return 'success';
  if (/progress|addressed|pending|deferred|overdue soon/.test(v)) return 'warning';
  if (/overdue|declined|missed|critical|no\b/.test(v)) return 'error';
  return 'neutral';
}

export const Badge: React.FC<{ children: React.ReactNode; tone?: Tone; mono?: boolean }> = ({
  children,
  tone = 'neutral',
  mono,
}) => (
  <span
    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
    style={{
      color: TONE_VAR[tone].fg,
      background: TONE_VAR[tone].bg,
      border: `1px solid ${TONE_VAR[tone].fg}`,
      fontFamily: mono ? 'var(--wf-mono, ui-monospace, Menlo, monospace)' : undefined,
    }}
  >
    {children}
  </span>
);

/**
 * The "why we ask" note as a hover/focus tip rather than a line of text.
 *
 * `tabIndex` and `aria-label` are not decoration: the design puts this on an `i`
 * glyph, and a glyph with a title attribute alone is unreachable by keyboard and
 * silent to a screen reader. The text is ALSO rendered to assistive tech, so the
 * guidance is never only available to a mouse.
 */
export const FieldTip: React.FC<{ text: string }> = ({ text }) => (
  <span
    tabIndex={0}
    role="note"
    aria-label={text}
    title={text}
    className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center align-middle"
    style={{ color: 'var(--wf-accent, #2563EB)' }}
  >
    <Info className="h-4 w-4" aria-hidden="true" />
  </span>
);

/**
 * A value the backend derived — an AUDIT-C total, a MIF production date.
 *
 * Shown as TEXT, never an input. These are `type='computed'` com_fields whose
 * value apply_computed_fields rewrites on every save, so an editable box would
 * invite an edit the next save silently discards.
 */
export const ComputedValue: React.FC<{ value: any; suffix?: string }> = ({ value, suffix }) => {
  const empty = value === null || value === undefined || value === '';
  return (
    <div
      className="rounded-md px-3 py-2 text-sm font-semibold"
      style={{
        background: 'var(--wf-accent-soft, #F3F4F6)',
        color: empty ? 'var(--wf-muted, #9CA3AF)' : 'var(--wf-text, #111827)',
        fontFamily: 'var(--wf-mono, ui-monospace, Menlo, monospace)',
      }}
    >
      {empty ? '—' : String(value)}
      {!empty && suffix ? <span className="ml-1 text-xs font-normal">{suffix}</span> : null}
    </div>
  );
};

/** A status as a toned pill. Its own component, not a branch inside
 *  renderFormElement, because the empty-state word needs the translation hook and
 *  a plain function cannot call one. */
export const StatusPill: React.FC<{ value: any }> = ({ value }) => {
  const { t } = useAppTranslation();
  const text = String(value ?? '');
  return <Badge tone={toneForStatus(text)}>{text || t('forms.not_started', 'Not started')}</Badge>;
};

/** "4 of 7 needs resolved", with the bar. */
export const ProgressBar: React.FC<{ done: number; total: number; label?: string }> = ({
  done,
  total,
  label,
}) => {
  const safeTotal = total > 0 ? total : 0;
  const pct = safeTotal ? Math.round((Math.min(done, safeTotal) / safeTotal) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="font-medium" style={{ color: 'var(--wf-text, #111827)' }}>
          {label || `${done} of ${safeTotal}`}
        </span>
        <span className="text-xs" style={{ color: 'var(--wf-muted, #6B7280)' }}>
          {pct}%
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full"
        style={{ background: 'var(--wf-border, #E5E7EB)' }}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, background: 'var(--wf-accent, #2563EB)' }}
        />
      </div>
    </div>
  );
};

/** A banner inside a section — "a new MIF file arrived after this form was started". */
export const InlineAlert: React.FC<{ tone?: Tone; title?: string; children?: React.ReactNode }> = ({
  tone = 'warning',
  title,
  children,
}) => (
  <div
    role="status"
    className="rounded-md px-3 py-2 text-sm"
    style={{
      background: TONE_VAR[tone].bg,
      color: 'var(--wf-text, #111827)',
      borderLeft: `3px solid ${TONE_VAR[tone].fg}`,
    }}
  >
    {title ? <div className="font-semibold">{title}</div> : null}
    {children ? <div className="mt-0.5">{children}</div> : null}
  </div>
);

/**
 * A value with what it used to be beside it.
 *
 * The design uses this where a new MIF file changed a field after the form was
 * started, so the LCM can see they are looking at something that moved under them.
 * The previous value is struck through, not hidden — the point is the comparison.
 */
export const ChangedValue: React.FC<{ value: any; was: any }> = ({ value, was }) => {
  const { t } = useAppTranslation();
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-sm">
      <span className="font-medium" style={{ color: 'var(--wf-text, #111827)' }}>
        {value === '' || value == null ? '\u2014' : String(value)}
      </span>
      {was !== undefined && was !== null && was !== '' && (
        <span className="text-xs line-through" style={{ color: 'var(--wf-muted, #9CA3AF)' }}>
          {t('forms.previous_value', 'was {{value}}', { value: String(was) })}
        </span>
      )}
    </div>
  );
};

/** The member's goal in their own words, quoted. Read-only; the LCM turns it into
 *  a goal elsewhere. Rendered as a blockquote so it is not mistaken for guidance. */
export const MemberVoice: React.FC<{ quote: string; attribution?: string }> = ({
  quote,
  attribution,
}) => (
  <blockquote
    className="rounded-md px-3 py-2 text-sm italic"
    style={{
      background: 'var(--wf-accent-soft, #F9FAFB)',
      borderLeft: '3px solid var(--wf-accent, #2563EB)',
      color: 'var(--wf-text, #111827)',
    }}
  >
    {`\u201C${quote}\u201D`}
    {attribution ? (
      <footer className="mt-1 text-xs not-italic" style={{ color: 'var(--wf-muted, #6B7280)' }}>
        — {attribution}
      </footer>
    ) : null}
  </blockquote>
);

/**
 * A bordered panel grouping the fields under it — the Member Snapshot's A–H.
 *
 * This renders the HEADING and its frame; the fields that follow are laid out by
 * the renderer as usual. A self-closing panel marker rather than a wrapper,
 * because `sections[].fields[]` is a flat list and nesting it would change the
 * template shape every other consumer reads.
 */
export const PanelHeading: React.FC<{ label: string; help?: string }> = ({ label, help }) => (
  <div
    className="mt-2 rounded-t-md px-3 py-2"
    style={{
      background: 'var(--wf-accent-soft, #F9FAFB)',
      borderTop: '1px solid var(--wf-border, #E5E7EB)',
      borderLeft: '1px solid var(--wf-border, #E5E7EB)',
      borderRight: '1px solid var(--wf-border, #E5E7EB)',
    }}
  >
    <div
      className="text-sm font-semibold"
      style={{ color: 'var(--wf-text, #111827)', fontFamily: 'var(--wf-heading-font, inherit)' }}
    >
      {label}
    </div>
    {help ? (
      <div className="mt-0.5 text-xs" style={{ color: 'var(--wf-muted, #6B7280)' }}>
        {help}
      </div>
    ) : null}
  </div>
);

/**
 * Yes/No per item, in rows — the design's checklist.
 *
 * Display-only here: the editable version is a multiselect the platform already
 * has. This renders what was answered, which is what a submitted record needs.
 */
export const ChecklistGrid: React.FC<{ items: string[]; selected: any }> = ({ items, selected }) => {
  const { t } = useAppTranslation();
  const chosen = new Set(
    (Array.isArray(selected) ? selected : String(selected ?? '').split(','))
      .map((v) => String(v).trim())
      .filter(Boolean),
  );
  return (
    <div className="divide-y rounded-md border" style={{ borderColor: 'var(--wf-border, #E5E7EB)' }}>
      {items.map((item) => (
        <div key={item} className="flex items-center justify-between px-3 py-1.5 text-sm">
          <span style={{ color: 'var(--wf-text, #111827)' }}>{item}</span>
          <Badge tone={chosen.has(item) ? 'success' : 'neutral'}>
            {chosen.has(item) ? t('common.yes', 'Yes') : t('common.no', 'No')}
          </Badge>
        </div>
      ))}
    </div>
  );
};

/**
 * A read-only answer, shown as a VALUE rather than a dead control.
 *
 * A disabled <select> still looks like a select: it has a chevron, it invites a
 * click, and it does nothing. IFR's Member Snapshot is 100 plan-owned fields
 * (TRUS-571) and rendering them as disabled inputs made the whole first screen
 * look like a form that had stopped working. The design shows them as labelled
 * values, which is what they are — the plan owns them and corrections go through
 * SFHP Customer Service, not this screen.
 *
 * A bordered tile rather than bare text, so a row of them reads as the design's
 * snapshot panel instead of a wall of loose words.
 */
export const ReadOnlyValue: React.FC<{ value: any; options?: string[] | null }> = ({ value }) => {
  const list = Array.isArray(value) ? value : null;
  const text = list ? list.join(', ') : value == null || value === '' ? '' : String(value);
  return (
    <div
      className="min-h-[2.25rem] rounded-md px-3 py-2 text-sm"
      style={{
        background: 'var(--wf-accent-soft, #F9FAFB)',
        border: '1px solid var(--wf-border, #E5E7EB)',
        color: text ? 'var(--wf-text, #111827)' : 'var(--wf-muted, #9CA3AF)',
      }}
    >
      {text || '\u2014'}
    </div>
  );
};

/**
 * A repeatable group, read-only.
 *
 * FormRenderer used to draw a grey box saying "Repeatable field group (available
 * in live form)" — so on IFR's care plan, SIX sections (Identified Needs, Goals
 * and Action Plan, Community Supports, Care Team, Care Plan Reviews) were
 * invisible in every preview and on every submitted record. Only
 * FormInstanceEditor mounted the real RepeatableGroupRenderer, which is the same
 * renderer split that has bitten this ticket three times now.
 *
 * It is not simply mounted here: RepeatableGroupRenderer is built on
 * react-hook-form's useFormContext/useFieldArray, and FormRenderer is a
 * controlled component with no FormProvider. Editing a group through
 * FormRenderer still needs that integration. What this fixes is SHOWING one —
 * which is what a preview and a submitted record need, and what was broken.
 *
 * With no rows it renders the group's SHAPE (its child labels), because a
 * preview exists to show what the form will look like, and an empty state that
 * says "none yet" tells a reviewer nothing about the section they are reviewing.
 */
export const ReadOnlyFieldGroup: React.FC<{
  groupId: number;
  label?: string;
  rows: any;
}> = ({ groupId, label, rows }) => {
  const { t } = useAppTranslation();
  const [children, setChildren] = React.useState<Array<{ name: string; friendly_name?: string }> | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!groupId) return;
    import('@/services/api/fieldGroups')
      .then((m) => m.fieldGroupsAPI.get(groupId))
      .then((g: any) => {
        if (!cancelled) setChildren(g?.children || g?.fields || []);
      })
      .catch(() => {
        // A group the API will not return must not blank the section it sits in.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  const childList = children || [];
  const labelFor = (c: any) => c.friendly_name || c.label || c.name;

  if (failed) {
    return (
      <div className="rounded-md px-3 py-2 text-sm" style={{ color: 'var(--wf-muted, #6B7280)' }}>
        {t('forms.groupUnavailable', 'This section could not be loaded.')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {list.length === 0 ? (
        <div
          className="rounded-md p-3"
          style={{ border: '1px dashed var(--wf-border, #E5E7EB)', background: 'var(--wf-accent-soft, #F9FAFB)' }}
        >
          <div className="mb-2 text-xs font-medium" style={{ color: 'var(--wf-muted, #6B7280)' }}>
            {t('forms.groupEmpty', 'No entries yet — each one records:')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {childList.map((c: any) => (
              <Badge key={c.name} tone="neutral">
                {labelFor(c)}
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        list.map((row, i) => (
          <div
            key={i}
            className="rounded-md p-3"
            style={{ border: '1px solid var(--wf-border, #E5E7EB)', background: 'var(--wf-surface, #FFFFFF)' }}
          >
            <div className="mb-2 text-xs font-semibold" style={{ color: 'var(--wf-muted, #6B7280)' }}>
              {label} {i + 1}
            </div>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              {childList.map((c: any) => {
                const v = row[c.name];
                const text = Array.isArray(v) ? v.join(', ') : v == null || v === '' ? '' : String(v);
                return (
                  <div key={c.name} className="flex gap-2 text-sm">
                    <dt className="shrink-0" style={{ color: 'var(--wf-muted, #6B7280)' }}>
                      {labelFor(c)}:
                    </dt>
                    <dd style={{ color: text ? 'var(--wf-text, #111827)' : 'var(--wf-muted, #9CA3AF)' }}>
                      {text || '\u2014'}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        ))
      )}
    </div>
  );
};

/**
 * The one entry point both renderers call.
 *
 * Returns null for anything it does not own, so each renderer can try this first
 * and fall through to its existing switch untouched. That is what keeps the two
 * from drifting again: there is one list of element types, in one file.
 */
export function renderFormElement(field: any, value: any): React.ReactNode | null {
  const f = field || {};
  const settings = f.settings || {};
  switch (f.field_type) {
    case 'panel_heading':
      return <PanelHeading label={f.label} help={f.help_text} />;
    case 'computed_display':
      return <ComputedValue value={value} suffix={settings.suffix} />;
    case 'badge':
      return <Badge tone={settings.tone || 'accent'}>{value || f.label}</Badge>;
    case 'status_pill':
      return <StatusPill value={value} />;
    case 'progress_bar':
      return (
        <ProgressBar
          done={Number(settings.done ?? 0)}
          total={Number(settings.total ?? 0)}
          label={f.label}
        />
      );
    case 'inline_alert':
      return (
        <InlineAlert tone={settings.tone || 'warning'} title={f.label}>
          {f.help_text}
        </InlineAlert>
      );
    case 'changed_value':
      return <ChangedValue value={value} was={settings.was} />;
    case 'member_voice':
      return <MemberVoice quote={String(value ?? '')} attribution={settings.attribution} />;
    case 'checklist_grid':
      return <ChecklistGrid items={f.options || []} selected={value} />;
    default:
      return null;
  }
}

/** The element types this module owns. Exported so a test can assert both
 *  renderers mount the same set — the drift guard. */
export const FORM_ELEMENT_TYPES = [
  'panel_heading',
  'computed_display',
  'badge',
  'status_pill',
  'progress_bar',
  'inline_alert',
  'changed_value',
  'member_voice',
  'checklist_grid',
] as const;
